import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Check, ChevronRight } from "lucide-react";
import { saveSession } from "@/lib/auth.js";
import { useTenant, TenantProvider } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.js";
import { Select } from "@/components/ui/select.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { useToast } from "@/components/ui/toast.js";
import { cn } from "@/lib/cn.js";
import type { Resource } from "@/lib/types.js";

/**
 * Onboarding wizard per Flow A continuation. After operator-driven
 * `POST /api/v1/admin/tenants` provisions the tenant, the owner clicks an email
 * link landing here at `/t/{slug}/setup?email={primary_owner_email}`. The wizard:
 *
 *   1. Saves a local-mode session with role=owner.
 *   2. Step "Location" — captures the primary location.
 *   3. Step "Schedule" — sets weekly hours on the first resource.
 *   4. Step "Payments" — surfaces Stripe Connect stub fields (modeled only; live
 *      integration deferred per PRD non-goal).
 *   5. Step "Done" — redirects to the default vertical view.
 *
 * Subsequent visits to `/setup` skip the welcome step but the wizard remains
 * usable for completing skipped steps.
 */
export function SetupPage(): React.JSX.Element {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Bootstrap session from query params on first arrival, if not already present.
  useEffect(() => {
    if (!slug) return;
    const params = new URLSearchParams(location.search);
    const email = params.get("email");
    if (email) {
      saveSession({
        tenantSlug: slug,
        actorId: `local-jwt-${slug}-owner`,
        email,
        role: "owner",
        createdAt: new Date().toISOString()
      });
    }
  }, [slug, location.search]);

  if (!slug) {
    navigate("/", { replace: true });
    return <div />;
  }

  return (
    <TenantProvider tenantSlug={slug} actorId={`local-jwt-${slug}-owner`}>
      <SetupShell />
    </TenantProvider>
  );
}

const STEPS = [
  { key: "welcome", title: "Welcome" },
  { key: "location", title: "Location" },
  { key: "schedule", title: "Schedule" },
  { key: "payments", title: "Payments" },
  { key: "done", title: "Done" }
] as const;
type StepKey = (typeof STEPS)[number]["key"];

function SetupShell(): React.JSX.Element {
  const tenant = useTenant();
  const navigate = useNavigate();
  const [step, setStep] = useState<StepKey>("welcome");

  if (tenant.loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-8">
        <Skeleton className="h-72 w-full max-w-xl" />
      </div>
    );
  }

  if (tenant.error) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-2 p-6 text-center">
        <h1 className="text-xl font-semibold">Could not load tenant</h1>
        <p className="text-sm text-muted-foreground">{tenant.error.message}</p>
        <Button variant="outline" onClick={tenant.reload}>Retry</Button>
      </div>
    );
  }

  const stepIndex = STEPS.findIndex((s) => s.key === step);

  function next(): void {
    const nextIndex = Math.min(STEPS.length - 1, stepIndex + 1);
    const nextKey = STEPS[nextIndex]!.key;
    if (nextKey === "done") {
      // Redirect to the appropriate default view.
      const view = tenant.profile?.default_view ?? "grid";
      const target =
        view === "grid" ? "calendar" : view === "dispatch" ? "dispatch" : "day-list";
      navigate(`/${target}`, { replace: true });
      return;
    }
    setStep(nextKey);
  }

  function back(): void {
    const prevIndex = Math.max(0, stepIndex - 1);
    setStep(STEPS[prevIndex]!.key);
  }

  return (
    <div className="relative min-h-dvh overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 grid-pattern opacity-50" />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -z-10 h-80 w-[640px] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl"
      />
      <div className="mx-auto flex min-h-dvh max-w-2xl flex-col gap-6 p-4 py-10 sm:p-8 sm:py-12">
        <header className="flex flex-col gap-3">
          <Badge variant="outline" className="self-start capitalize">
            Tenant setup · {tenant.me?.tenant.vertical}
          </Badge>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {tenant.me?.tenant.slug ?? tenant.tenantSlug}
          </h1>
          <p className="text-sm text-muted-foreground">
            A short guided walk-through. Anything skipped is editable later from the admin pages.
          </p>
        </header>

        <Stepper current={step} onSelect={setStep} />

        {step === "welcome" && <WelcomeStep onNext={next} />}
        {step === "location" && <LocationStep onNext={next} onBack={back} />}
        {step === "schedule" && <ScheduleStep onNext={next} onBack={back} />}
        {step === "payments" && <PaymentsStep onNext={next} onBack={back} />}
      </div>
    </div>
  );
}

function Stepper({
  current,
  onSelect
}: {
  current: StepKey;
  onSelect: (s: StepKey) => void;
}): React.JSX.Element {
  return (
    <ol className="flex flex-wrap gap-1.5 text-xs">
      {STEPS.filter((s) => s.key !== "done").map((s, i) => {
        const isActive = s.key === current;
        const isDone = STEPS.findIndex((x) => x.key === current) > i;
        return (
          <li key={s.key}>
            <button
              type="button"
              onClick={() => onSelect(s.key)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border border-border-strong bg-card px-2.5 py-1 font-medium transition-colors duration-150 cursor-pointer hover:bg-accent",
                isActive && "border-primary bg-primary/10 text-primary",
                isDone && "border-success/30 bg-success/10 text-success"
              )}
            >
              <span
                className={cn(
                  "inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : isDone
                      ? "bg-success text-success-foreground"
                      : "bg-muted text-muted-foreground"
                )}
              >
                {isDone ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {s.title}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function WelcomeStep({ onNext }: { onNext: () => void }): React.JSX.Element {
  const tenant = useTenant();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Welcome to your calendar</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-foreground">
          A few quick steps to get your tenant ready. You can come back any time —
          everything below is editable later from the admin pages.
        </p>
        <ul className="ml-4 list-disc text-muted-foreground">
          <li>Confirm your primary location and address.</li>
          <li>Set weekly working hours for your first resource.</li>
          <li>Review payment settings (Stripe Connect — modeled, not live).</li>
        </ul>
        {tenant.profile && (
          <div className="rounded border bg-muted/40 p-3 text-xs">
            Default view for the {tenant.me?.tenant.vertical} vertical:{" "}
            <code>{tenant.profile.default_view}</code>.
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={onNext}>
            Begin
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LocationStep({
  onNext,
  onBack
}: {
  onNext: () => void;
  onBack: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const existing = useAsync(() => api.listLocations(), [api]);
  const first = existing.data?.data?.[0];
  const [name, setName] = useState(first?.name ?? "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (first) setName(first.name);
  }, [first]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Primary location</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-foreground">
          Name the place where your customers will be served. For trades tenants,
          this is the dispatch hub.
        </p>
        <div className="flex flex-col gap-1">
          <Label htmlFor="loc-name">Location name</Label>
          <Input
            id="loc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Demo Salon"
          />
        </div>
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={onBack}>Back</Button>
          <Button
            disabled={!name || submitting}
            onClick={async () => {
              setSubmitting(true);
              try {
                if (first) {
                  // No PATCH endpoint for locations in v1 — reuse the existing row.
                } else {
                  await api.createLocation({ name });
                  toast({ title: "Location created", variant: "success" });
                }
                onNext();
              } catch (error) {
                toast({
                  title: "Could not save",
                  description: String(error),
                  variant: "destructive"
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? "Saving…" : "Continue"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ScheduleStep({
  onNext,
  onBack
}: {
  onNext: () => void;
  onBack: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const resources = useAsync(() => api.listResources(), [api]);
  const [resourceId, setResourceId] = useState<string>("");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [submitting, setSubmitting] = useState(false);

  const visibleResources = useMemo<Resource[]>(
    () => (resources.data?.data ?? []).filter((r) => !r.deleted_at),
    [resources.data]
  );

  useEffect(() => {
    if (!resourceId && visibleResources[0]) setResourceId(visibleResources[0].id);
  }, [visibleResources, resourceId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Working hours</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-foreground">
          Set Monday–Friday hours for your first resource. You can refine schedules
          per-resource and add holidays later from the admin pages.
        </p>
        {visibleResources.length === 0 ? (
          <div className="rounded border border-dashed p-4 text-center text-xs text-muted-foreground">
            No resources yet. Skip this step and add one from the Resources page.
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <Label>Resource</Label>
              <Select value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
                {visibleResources.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Label htmlFor="start">Start</Label>
                <Input id="start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="end">End</Label>
                <Input id="end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
          </>
        )}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={onBack}>Back</Button>
          <Button
            disabled={(visibleResources.length > 0 && !resourceId) || submitting}
            onClick={async () => {
              if (visibleResources.length === 0) {
                onNext();
                return;
              }
              setSubmitting(true);
              try {
                const intervals: Array<[string, string]> = [[start, end]];
                await api.putSchedule(resourceId, {
                  resource_id: resourceId,
                  weekly_hours: {
                    monday: intervals,
                    tuesday: intervals,
                    wednesday: intervals,
                    thursday: intervals,
                    friday: intervals
                  },
                  overrides: {},
                  time_off: []
                });
                toast({ title: "Hours saved", variant: "success" });
                onNext();
              } catch (error) {
                toast({
                  title: "Could not save",
                  description: String(error),
                  variant: "destructive"
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? "Saving…" : "Continue"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentsStep({
  onNext,
  onBack
}: {
  onNext: () => void;
  onBack: () => void;
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payments — Stripe Connect</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-foreground">
          Stripe Connect is modeled in the schema (deposits, no-show fees, payout
          accounts) but the live integration is deferred — see the PRD non-goal.
          You can configure stub deposit and no-show amounts on each service today;
          actual charging is unavailable in v1.
        </p>
        <div className="rounded-md border bg-muted/40 p-3 text-xs">
          To enable live charges, contact your Ronic platform operator. Once
          enabled, this step will surface a Connect onboarding link.
        </div>
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={onBack}>Back</Button>
          <Button onClick={onNext}>
            Finish
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
